import { motion } from "framer-motion";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Download, FileText, ArrowRight, X } from "lucide-react";
import { useMediaKit, downloadHref, isOwnFile } from "@/lib/mediaKit";

const BIO_LABELS = { short: "Short bio", medium: "Medium bio", long: "Long bio" };

const BioCard = ({ kind, text }) => (
  <div className="bg-[var(--bg-card)] border-glow rounded-xl p-6 flex flex-col" data-testid={`media-bio-${kind}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs uppercase tracking-[0.2em] text-[var(--accent-violet-light)] font-semibold">{BIO_LABELS[kind]}</span>
      <span className="text-[10px] text-[var(--text-secondary)]">{text.split(/\s+/).filter(Boolean).length} words</span>
    </div>
    <p className="text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-line flex-1">{text}</p>
    <button onClick={() => { navigator.clipboard.writeText(text); toast.success(`${BIO_LABELS[kind]} copied`); }} className="mt-5 self-start inline-flex items-center gap-1.5 text-sm text-white border-glow px-4 py-2 rounded-full hover:bg-[var(--accent-violet)]/10 transition-colors" data-testid={`copy-bio-${kind}`}>
      <Copy className="w-3.5 h-3.5" /> Copy
    </button>
  </div>
);

const Lightbox = ({ item, onClose }) => (
  <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose} data-testid="media-lightbox">
    <button className="absolute top-6 right-6 text-white/70 hover:text-white" onClick={onClose} data-testid="media-lightbox-close"><X className="w-6 h-6" /></button>
    <figure className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
      <img src={item.url} alt={item.caption} className="w-full max-h-[80vh] object-contain rounded-xl" />
      {item.caption && <figcaption className="text-center text-sm text-[var(--text-secondary)] mt-3">{item.caption}</figcaption>}
    </figure>
  </div>
);

const Gallery = ({ items }) => {
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const shown = items.filter((g) => filter === "all" || g.kind === filter);
  return (
    <section className="mb-24">
      <div className="flex items-end justify-between mb-8">
        <h2 className="font-display text-3xl md:text-4xl font-bold text-white italic">Photo Gallery</h2>
        <div className="flex gap-2">
          {["all", "stage", "portrait"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`px-4 py-1.5 rounded-full text-sm capitalize transition-colors ${filter === f ? "bg-[var(--accent-violet)] text-white" : "text-[var(--text-secondary)] hover:text-white border-glow"}`} data-testid={`gallery-filter-${f}`}>{f}</button>
          ))}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        {shown.map((g, i) => (
          <motion.button key={g.url + i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} onClick={() => setOpen(g)} className="group relative aspect-video rounded-xl overflow-hidden text-left" data-testid={`gallery-photo-${i}`}>
            <img src={g.url} alt={g.caption} loading="lazy" className="w-full h-full object-cover object-[center_22%] transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between">
              <span className="text-sm text-white">{g.caption}</span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--accent-violet-light)]">{g.kind}</span>
            </div>
          </motion.button>
        ))}
      </div>
      {open && <Lightbox item={open} onClose={() => setOpen(null)} />}
    </section>
  );
};

const Headshots = ({ items }) => (
  <section className="mb-24">
    <h2 className="font-display text-3xl md:text-4xl font-bold text-white italic mb-2">Headshots</h2>
    <p className="text-[var(--text-secondary)] mb-8 text-sm">High-resolution images approved for event promotion and press use.</p>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {items.map((h, i) => (
        <div key={h.url + i} className="bg-[var(--bg-card)] border-glow rounded-xl overflow-hidden" data-testid={`headshot-${i}`}>
          <img src={h.url} alt={h.label} loading="lazy" className="w-full aspect-video object-cover object-[center_22%]" />
          <div className="p-4 flex items-center justify-between">
            <span className="text-sm text-white">{h.label}</span>
            <a href={downloadHref(h.url)} {...(isOwnFile(h.url) ? { download: true } : { target: "_blank", rel: "noopener noreferrer" })} className="inline-flex items-center gap-1.5 text-sm text-[var(--accent-violet-light)] hover:text-white transition-colors" data-testid={`download-headshot-${i}`}>
              <Download className="w-4 h-4" /> Download
            </a>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const MediaPage = () => {
  const kit = useMediaKit();
  if (!kit) return <main className="pt-24 min-h-screen" data-testid="media-loading" />;
  const oneSheet = kit.one_sheet_url;

  return (
    <main className="pt-28 pb-20 min-h-screen" data-testid="media-page">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-16 max-w-3xl">
          <p className="text-sm uppercase tracking-[0.25em] text-[var(--accent-violet-light)] font-medium mb-4">Media & Press</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white italic mb-5" data-testid="media-heading">Media Kit</h1>
          <p className="text-[var(--text-secondary)] text-base md:text-lg">Everything event organizers, producers and journalists need: approved bios, photography, headshots and the speaker one-sheet.</p>
          <div className="flex flex-wrap gap-3 mt-8">
            {oneSheet ? (
              <a href={downloadHref(oneSheet)} download className="inline-flex items-center gap-2 bg-[var(--accent-violet)] hover:bg-[#6d28d9] text-white px-6 py-3 rounded-full font-semibold text-sm transition-colors" data-testid="download-one-sheet-btn"><FileText className="w-4 h-4" /> Download Speaker One-Sheet</a>
            ) : (
              <Link to="/media/one-sheet" className="inline-flex items-center gap-2 bg-[var(--accent-violet)] hover:bg-[#6d28d9] text-white px-6 py-3 rounded-full font-semibold text-sm transition-colors" data-testid="download-one-sheet-btn"><FileText className="w-4 h-4" /> Speaker One-Sheet (PDF)</Link>
            )}
            <Link to="/contact" className="inline-flex items-center gap-2 border border-[rgba(124,58,237,0.4)] hover:border-[var(--accent-violet)] text-white px-6 py-3 rounded-full text-sm transition-colors" data-testid="media-inquiry-btn">Media Inquiry <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </motion.div>

        {kit.gallery.length > 0 && <Gallery items={kit.gallery} />}

        <section className="mb-24">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white italic mb-8">Press Bios</h2>
          <div className="grid lg:grid-cols-3 gap-5">
            {["short", "medium", "long"].filter((k) => kit.bios[k]).map((k) => <BioCard key={k} kind={k} text={kit.bios[k]} />)}
          </div>
        </section>

        {kit.headshots.length > 0 && <Headshots items={kit.headshots} />}
      </div>
    </main>
  );
};

export default MediaPage;
